import React from 'react';
import { StyleSheet, View } from 'react-native';
import { colors, radii, screenPadding, spacing } from '../../theme';
import Button, { ButtonVariant } from './Button';
import Icon, { IconName } from './Icon';
import Text from './Text';

/**
 * The request worked, and the honest answer is "nothing".
 *
 * The counterpart to `ErrorState`, and the distinction between the two is the
 * whole point - see the note there. This one is calm on purpose: no danger
 * ink, no alarm, because nothing has gone wrong. An empty cart is a cart you
 * have not filled yet.
 *
 * Written once because the app already had two hand-built versions of it -
 * the cart's and the bookings list's - each with its own icon size, its own
 * gaps and its own button. The same idea drawn twice is how a set of screens
 * stops feeling like one app.
 *
 * The "book again" rail's empty strip is deliberately NOT this: it lays the
 * line and the button side by side inside a band, rather than stacking them
 * in the middle of a page. Different shape, different job - forcing it
 * through here would make a rail look like a failed screen.
 *
 * The action is optional but almost always worth having. An empty state that
 * only states the emptiness leaves someone on a dead end; one that offers the
 * thing they would have to go and find anyway saves them the trip.
 */

type Props = {
  /** Sets the tone before a word is read - a cart, a calendar, a bell. */
  icon: IconName;
  title: string;
  /** One line on why it is empty, and ideally what fills it. */
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionVariant?: ButtonVariant;
  /** Sits in a band inside a page rather than filling the screen. */
  compact?: boolean;
};

function EmptyState({
  icon,
  title,
  message,
  actionLabel,
  onAction,
  actionVariant = 'outline',
  compact = false,
}: Props) {
  return (
    <View
      style={compact ? styles.compact : styles.root}
      accessible
      accessibilityLabel={[title, message].filter(Boolean).join('. ')}
    >
      {/*
        A tinted circle, the way `ErrorState`'s `mark` does it - not the bare
        stroked glyph this used to float on its own. A status badge would be
        the wrong read for "empty" as much as for "error", which is why the
        tint is the calm brand tone rather than red: same shape as the error
        mark, different colour for a different fact.
      */}
      <View style={compact ? styles.markSm : styles.mark}>
        <Icon
          name={icon}
          size={compact ? 20 : 30}
          color={colors.primary}
          strokeWidth={1.5}
        />
      </View>

      <Text variant={compact ? 'labelSm' : 'subtitle'} align="center">
        {title}
      </Text>

      {message ? (
        <Text variant="bodySm" color={colors.inkMuted} align="center">
          {message}
        </Text>
      ) : null}

      {actionLabel && onAction ? (
        <Button
          label={actionLabel}
          variant={actionVariant}
          size={compact ? 'sm' : 'md'}
          fullWidth={false}
          onPress={onAction}
          style={styles.action}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.xxxl,
  },
  compact: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.xl,
  },
  mark: {
    width: 64,
    height: 64,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    marginBottom: spacing.xs,
  },
  markSm: {
    width: 40,
    height: 40,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  action: {
    marginTop: spacing.sm,
    /*
     * Centred explicitly, because the button centres itself nowhere.
     *
     * `fullWidth={false}` gives it `alignSelf: 'flex-start'`, and a child's
     * own `alignSelf` beats the parent's `alignItems: 'center'` - so the
     * heading and the message sat centred while the button hugged the left
     * edge underneath them. This is applied after the button's own styles,
     * which is what lets it win.
     */
    alignSelf: 'center',
  },
});

export default EmptyState;
